import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faTwitter, faTiktok, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faCheck, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { AuroraBackground } from '../ui/aurora-background';
import { businessAuthAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface SocialMediaUsernameSetupProps {
  onComplete: () => void;
}

interface ProfileData {
  id?: string;
  username?: string;
  displayName?: string;
  followerCount?: number;
  followingCount?: number;
  mediaCount?: number;
  accountType?: string;
  profileImageUrl?: string;
  verified?: boolean;
}

interface PlatformState {
  username: string;
  loading: boolean;
  profile: ProfileData | null;
  error: string | null;
  verified: boolean;
  lastVerifiedUsername?: string | null;
}

const SocialMediaUsernameSetup: React.FC<SocialMediaUsernameSetupProps> = ({ onComplete }) => {
  const { refreshBusinessProfile } = useAuth();
  const [platforms, setPlatforms] = useState<Record<string, PlatformState>>({
    facebook: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    instagram: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    twitter: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    tiktok: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    linkedin: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
  });

  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getProxyImageUrl = (url?: string) => {
    if (!url) return '';
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const platformConfig = {
    facebook: { icon: faFacebook, label: 'Facebook', placeholder: 'facebook_username' },
    instagram: { icon: faInstagram, label: 'Instagram', placeholder: 'instagram_username' },
    twitter: { icon: faTwitter, label: 'Twitter/X', placeholder: 'twitter_username' },
    tiktok: { icon: faTiktok, label: 'TikTok', placeholder: 'tiktok_username' },
    linkedin: { icon: faLinkedin, label: 'LinkedIn', placeholder: 'linkedin_username' },
  };

  const handleUsernameChange = (platform: string, value: string) => {
    setPlatforms(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        username: value,
        // Kullanıcı değiştiyse doğrulama durumunu resetle
        profile: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].profile : null,
        error: null,
        verified: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].verified : false,
      }
    }));
    setError(null);
  };

  const handleVerifyAll = async () => {
    // Doğrulanması gereken platformları topla: username girilmiş ve ya hiç doğrulanmamış ya da değişmiş
    const payload: Record<string, string> = {};
    Object.entries(platforms).forEach(([platform, state]) => {
      const trimmed = state.username.trim();
      if (trimmed && trimmed !== state.lastVerifiedUsername) {
        payload[platform] = trimmed;
      }
    });

    // Eğer doğrulanacak hiçbir şey yoksa direkt çık
    if (Object.keys(payload).length === 0) {
      return;
    }

    // Loading durumunu güncelle
    setVerifying(true);
    setPlatforms(prev => {
      const next = { ...prev };
      Object.keys(payload).forEach((platform) => {
        next[platform] = {
          ...next[platform],
        loading: true,
        error: null,
        };
      });
      return next;
    });
    setError(null);

    try {
      const response = await businessAuthAPI.getSocialMediaProfiles(payload);
      const respData = response?.data?.data || {};
      const respErrors = (response?.data as any)?.errors || {};

      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          const platformResp = (respData as any)[platform] || null;
          if (platformResp) {
            // Normalize response to our UI expectations
            const normalized: ProfileData = {
              ...platformResp,
              verified: true,
            };
            next[platform] = {
              ...next[platform],
            loading: false,
              profile: normalized,
            verified: true,
            error: null,
              lastVerifiedUsername: next[platform].username.trim(),
            };
      } else {
            const errorMsg = (respErrors as any)?.[platform] || 'Profil bulunamadı';
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: null,
              verified: false,
              error: errorMsg,
            };
          }
        });
        return next;
      });
    } catch (err: any) {
      // Genel hata
      const genericError = err?.response?.data?.message || 'Doğrulama başarısız';
      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          next[platform] = {
            ...next[platform],
            loading: false,
            profile: null,
            verified: false,
            error: err?.response?.data?.errors?.[platform] || genericError,
          };
        });
        return next;
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    // En az bir platform username'i girilmiş olmalı
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    if (!hasAnyUsername) {
      setError('Lütfen en az bir sosyal medya kullanıcı adı girin');
      return;
    }

    // En az bir platform doğrulanmış olmalı
    const hasAnyVerified = Object.values(platforms).some(p => p.verified);
    if (!hasAnyVerified) {
      setError('Lütfen en az bir sosyal medya hesabını doğrulayın');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Sadece doğrulanmış platformları gönder
      const payload: any = {};
      Object.entries(platforms).forEach(([platform, state]) => {
        if (state.verified && state.username.trim()) {
          payload[platform] = state.username.trim();
        }
      });

      await businessAuthAPI.addSocialMediaAccounts(payload);
      
      // Business profile'ı yenile
      await refreshBusinessProfile();
      
      // Arka planda sosyal medya verilerini senkronize etmeyi başlat (bekleme yok)
      try {
        // Beklemiyoruz ki kullanıcı direkt dashboard'a geçsin
        void businessAuthAPI.syncSocialMediaData();
      } catch (_e) {
        // Bu hata kullanıcı akışını engellememeli; sessizce geç
      }
      
      // Başarılı, veri çekme işlemini başlat
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Hesap kaydedilirken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
  const allEnteredAreVerified = Object.values(platforms).every(p => !p.username.trim() || p.verified);
  const primaryCtaIsContinue = hasAnyUsername && allEnteredAreVerified && !verifying;

  return (
    <AuroraBackground className="w-full h-full overflow-scroll">
      <div className="w-full min-h-screen flex items-center justify-center relative overflow-hidden py-10">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900"></div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/10 rounded-full blur-xl animate-float"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>

        <div className="relative z-10 w-full max-w-2xl mx-auto px-6">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">
                Sosyal medya Hesaplarınızı Bağlayın
              </h1>
              <p className="text-white/70">
                Analiz için en az bir sosyal medya hesabınızın kullanıcı adını girin
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Platform Inputs */}
            <div className="space-y-4 mb-6">
              {Object.entries(platformConfig).map(([platform, config]) => {
                const state = platforms[platform];
                return (
                  <div key={platform} className="space-y-2">
                    <label className="block text-sm font-medium text-white/80">
                      {config.label}
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FontAwesomeIcon icon={config.icon} className="text-white/40 text-sm" />
                        </div>
                        <input
                          type="text"
                          value={state.username}
                          onChange={(e) => handleUsernameChange(platform, e.target.value)}
                          placeholder={config.placeholder}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                          disabled={state.loading || submitting || verifying}
                        />
                      </div>
                    </div>
                    
                    {/* Profile Info or Error */}
                    {state.verified && state.profile && (
                      <div className="ml-0 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <div className="flex items-center gap-3">
                          {state.profile.profileImageUrl && (
                            <img
                              src={getProxyImageUrl(state.profile.profileImageUrl)}
                              alt={state.profile.displayName || state.username}
                              className="w-10 h-10 rounded-full"
                              referrerPolicy="no-referrer"
                              crossOrigin="anonymous"
                            />
                          )}
                          <div className="flex-1">
                            <p className="text-white font-medium">{state.profile.displayName || state.username}</p>
                            {state.profile.followerCount !== undefined && (
                              <p className="text-white/60 text-sm">
                                {state.profile.followerCount.toLocaleString()} takipçi
                              </p>
                            )}
                          </div>
                          {state.profile.verified && (
                            <FontAwesomeIcon icon={faCheck} className="text-blue-400" />
                          )}
                        </div>
                      </div>
                    )}
                    
                    {state.error && (
                      <div className="ml-0 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-red-400 text-sm">{state.error}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Primary CTA */}
            <button
              onClick={primaryCtaIsContinue ? handleSubmit : handleVerifyAll}
              disabled={
                !hasAnyUsername ||
                submitting ||
                verifying
              }
              className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  <span>Kaydediliyor...</span>
                </div>
              ) : verifying ? (
                <div className="flex items-center justify-center space-x-2">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  <span>Doğrulanıyor...</span>
                </div>
              ) : (
                primaryCtaIsContinue ? 'Devam Et' : 'Doğrula'
              )}
            </button>
          </div>
        </div>
      </div>
    </AuroraBackground>
  );
};

export default SocialMediaUsernameSetup;

